import { JSX } from 'react';
import orModelHomeGridVariants from './or-model-home-grid.variants';

// components
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import Skeleton from 'react-loading-skeleton';
import { MlCard } from '@/components/molecules';

// types
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import { EMlCardsTypes } from '@/components/molecules/ml-card/ml-card.types';
import { IAtMediaProps } from '@/components/atoms/at-media/at-media.types';
import { IOrmodelHomeGridProps } from './or-model-home-grid.types';
import { IHousePlanHit } from '@/lib/search/types';
import { ETagVariants } from '@/components/atoms/at-tag/at-tag.types';
import { IDesignStudio } from '../or-design-studio-block/or-design-studio-block.types';

// hooks + utils
import { parseImageFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';
import { formatModelHomeUrl } from '@/components/helpers/formatters/model-homes';

/**
 * `RenderGridSkeleton` is a lightweight placeholder component used to indicate loading state
 * while model home data is being fetched.
 *
 * It renders a top-level header skeleton followed by six card-sized skeletons in a responsive grid.
 * Styling uses `react-loading-skeleton` with custom colors and rounded corners.
 *
 */
export const RenderGridSkeleton = () => {
  return (
    <div className="container">
      <Skeleton
        baseColor="var(--color-neutral-100)"
        highlightColor="var(--color-neutral-200)"
        height={100}
        width={'60%'}
        className="rounded-30"
      />
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from(Array(6)).map((d, idx) => {
          return (
            <Skeleton
              key={idx}
              baseColor="var(--color-neutral-100)"
              highlightColor="var(--color-neutral-200)"
              height={200}
              className="flex flex-row rounded-30"
            />
          );
        })}
      </div>
    </div>
  );
};

/**
 * Renders an individual model home card using `MlCard` and formats its data from the provided plan object.
 *
 * Pulls image data from CMS if available, otherwise falls back to API image.
 * Location tags are derived from the CMS or API studio data.
 *
 */
const RenderPlanCards = ({
  plan,
  designStudios,
}: {
  plan: IHousePlanHit;
  designStudios: IDesignStudio[];
}) => {
  const { SquareFeet, Plan, ExteriorStyle, FloorPlans, Image, cms } = plan;
  const { Bedrooms, Bathrooms, Name, Id } = Plan;

  const studio = designStudios.find((s) => s.Id == FloorPlans?.[0]?.StudioId);

  const getTagContent = () => {
    const name = studio?.CsName || studio?.Name || null;
    // || FloorPlans?.[0]?.Studio?.Name.replace(/\\\//g, '-') || ==> this should NEVER happen -- the studio data will be aligned with the models from FD Data

    const state = studio?.CsState || studio?.Name || null;
    // || FloorPlans?.[0]?.Studio?.State || null; ==> this should NEVER happen -- the studio data will be aligned with the models from FD Data

    let base = '';
    if (name) {
      base = name;
    }

    if (state) {
      base = `${base}, ${state}`;
    }

    return base;
  };

  const imageProps: IAtMediaProps | undefined = cms?.card_image
    ?.mobile_image_url
    ? (parseImageFromCMS(cms?.card_image) as IAtMediaProps)
    : Image
      ? ({
          src: {
            mobile: Image,
            tablet: Image,
            desktop: Image,
          },
          alt: `${Name} - ${ExteriorStyle?.Name}`,
        } as IAtMediaProps)
      : undefined;

  return (
    imageProps?.src && (
      <MlCard
        headerElement="p"
        variant={EMlCardsTypes.HOME_PLAN}
        imageProps={imageProps!}
        href={formatModelHomeUrl(
          studio?.CsName || '',
          //|| FloorPlans?.[0].Studio.Name || '',
          Name,
          ExteriorStyle?.Name,
        )}
        showFavouriteButton={false}
        homePlanProps={{
          id: Id,
          name: Name,
          description: ExteriorStyle?.Name,
          features: [
            'Model Home',
            `${Bedrooms} Bed`,
            `${Bathrooms} Bath`,
            `${SquareFeet} Sq.Ft`,
          ],
          tags: [
            {
              variant: ETagVariants.LOCATION,
              children: `${getTagContent()}`,
            },
          ],
        }}
      />
    )
  );
};

/**
 * `OrModelHomeGrid` renders a grouped and responsive grid of model home cards.
 *
 * Each group (e.g., state or region) is rendered with a header and a 1–3 column grid of cards.
 * If no data is available, a fallback message is displayed. Optional top and bottom dividers can be added.
 *
 */
export const OrModelHomeGrid = ({
  modelHomeData,
  allDesignStudios,
  topSpacing = 'md',
  bottomDivider = false,
  topDivider = false,
  bottomSpacing = 'md',
  headingElement = 'h2',
}: IOrmodelHomeGridProps): JSX.Element => {
  const classes = orModelHomeGridVariants({ topSpacing, bottomSpacing });

  return (
    <div data-theme="light">
      <section className="container">
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
        {modelHomeData ? (
          <div className={`${classes}`}>
            {Object.entries(modelHomeData).map(([key, value]) => {
              return (
                <div key={key} className="pb-xl">
                  <AtHeader
                    element={headingElement}
                    variant="h2"
                    className="pb-6 lg:pb-13"
                  >
                    The {key}
                  </AtHeader>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                    {value.map((plans: IHousePlanHit) => {
                      return (
                        <div key={plans.objectID}>
                          <RenderPlanCards
                            plan={plans}
                            designStudios={allDesignStudios}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mx-10 flex flex-row justify-center">
            <AtHeader element="p" variant="h6">
              Sorry, no model homes to show at this time
            </AtHeader>
          </div>
        )}
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
      </section>
    </div>
  );
};
